'use client';
import React from 'react';
import { RotateCcw, BarChart3, Home, CheckCircle, XCircle, Clock, Zap } from 'lucide-react';
import { QuizSession } from './QuizArenaContent';

interface QuizResultsProps {
  session: QuizSession;
  quizData: { title: string; emoji: string; questions: { question: string; options: string[]; correctIndex: number; points: number }[] };
  onPlayAgain: () => void;
  onViewStats: () => void;
  onBackToLobby: () => void;
}

function getGrade(accuracy: number): { label: string; color: string; emoji: string } {
  if (accuracy >= 90) return { label: 'Outstanding!', color: '#F59E0B', emoji: '🏆' };
  if (accuracy >= 75) return { label: 'Excellent!', color: '#10B981', emoji: '🌟' };
  if (accuracy >= 60) return { label: 'Good Job!', color: '#06B6D4', emoji: '👍' };
  if (accuracy >= 40) return { label: 'Keep Practicing', color: '#F59E0B', emoji: '💪' };
  return { label: 'Try Again!', color: '#F43F5E', emoji: '📚' };
}

export default function QuizResults({ session, quizData, onPlayAgain, onViewStats, onBackToLobby }: QuizResultsProps) {
  const correct = session.attempts.filter(a => a.isCorrect).length;
  const wrong = session.attempts.filter(a => !a.isCorrect && a.selectedIndex !== null).length;
  const skipped = session.attempts.filter(a => a.selectedIndex === null).length;
  const accuracy = Math.round((correct / session.attempts.length) * 100);
  const avgTime = session.attempts.length > 0
    ? Math.round(session.totalTime / session.attempts.length)
    : 0;
  const grade = getGrade(accuracy);
  const maxPossible = quizData.questions.reduce((s, q) => s + q.points, 0);
  const scorePercent = Math.round((session.totalScore / maxPossible) * 100);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Result Hero */}
      <div className="rounded-2xl p-6 mb-6 text-center"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="text-5xl mb-3">{grade.emoji}</div>
        <h1 className="text-2xl font-extrabold mb-1" style={{ color: grade.color }}>{grade.label}</h1>
        <p className="text-sm mb-5" style={{ color: 'var(--muted-foreground)' }}>{session.title}</p>

        {/* Score Ring */}
        <div className="flex items-center justify-center mb-5">
          <div className="relative w-32 h-32">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="50" fill="none" stroke="var(--border)" strokeWidth="10" />
              <circle cx="60" cy="60" r="50" fill="none"
                stroke={grade.color} strokeWidth="10"
                strokeDasharray={`${2 * Math.PI * 50}`}
                strokeDashoffset={`${2 * Math.PI * 50 * (1 - scorePercent / 100)}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1s ease' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-extrabold text-foreground">{session.totalScore}</span>
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>pts</span>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Correct', value: correct, icon: CheckCircle, color: '#10B981' },
            { label: 'Wrong', value: wrong, icon: XCircle, color: '#F43F5E' },
            { label: 'Accuracy', value: `${accuracy}%`, icon: Zap, color: '#F59E0B' },
            { label: 'Avg Time', value: `${avgTime}s`, icon: Clock, color: '#06B6D4' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl p-3" style={{ background: 'var(--muted)' }}>
              <stat.icon size={16} style={{ color: stat.color, margin: '0 auto 4px' }} />
              <p className="text-base font-extrabold text-foreground">{stat.value}</p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Question Review */}
      <div className="rounded-2xl p-5 mb-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <h3 className="font-bold text-foreground mb-4">Question Review</h3>
        <div className="space-y-3">
          {session.attempts.map((attempt, i) => {
            const q = quizData.questions[i];
            if (!q) return null;
            return (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl"
                style={{ background: 'var(--muted)' }}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-sm"
                  style={{
                    background: attempt.isCorrect ? 'rgba(16,185,129,0.2)' : attempt.selectedIndex === null ? 'rgba(100,116,139,0.2)' : 'rgba(244,63,94,0.2)',
                    color: attempt.isCorrect ? '#10B981' : attempt.selectedIndex === null ? '#64748B' : '#F43F5E',
                  }}>
                  {attempt.isCorrect ? '✓' : attempt.selectedIndex === null ? '–' : '✗'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground leading-snug mb-1 line-clamp-1">{q.question}</p>
                  <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    <span>Correct: <span className="font-semibold" style={{ color: '#10B981' }}>{q.options[q.correctIndex]}</span></span>
                    {attempt.selectedIndex !== null && attempt.selectedIndex !== q.correctIndex && (
                      <span>Your answer: <span className="font-semibold" style={{ color: '#F43F5E' }}>{q.options[attempt.selectedIndex]}</span></span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-bold" style={{ color: attempt.isCorrect ? '#F59E0B' : 'var(--muted-foreground)' }}>
                    {attempt.isCorrect ? `+${attempt.pointsEarned}` : '0'}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{attempt.timeTaken}s</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-3 gap-3">
        <button onClick={onBackToLobby}
          className="flex flex-col items-center gap-1.5 py-3 rounded-xl font-semibold text-xs transition-all hover:opacity-80"
          style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
          <Home size={16} />
          Lobby
        </button>
        <button onClick={onViewStats}
          className="flex flex-col items-center gap-1.5 py-3 rounded-xl font-semibold text-xs transition-all hover:opacity-80"
          style={{ background: 'rgba(6,182,212,0.1)', color: '#06B6D4', border: '1px solid rgba(6,182,212,0.2)' }}>
          <BarChart3 size={16} />
          My Stats
        </button>
        <button onClick={onPlayAgain}
          className="flex flex-col items-center gap-1.5 py-3 rounded-xl font-bold text-xs transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>
          <RotateCcw size={16} />
          Play Again
        </button>
      </div>
    </div>
  );
}
